/**
 * Audit Service
 * Records all significant operations for compliance and traceability
 * Captures device/browser info for forensic analysis (Rule #12)
 *
 * Every write operation in the system should call auditService.log()
 */
import { collection, addDoc, Timestamp } from 'firebase/firestore';
import { examDb } from '../../config/firebase';
import { COLLECTIONS } from '../../config/constants';

class AuditService {
  /**
   * Capture device/browser fingerprint from navigator
   * @returns {Object} device info
   */
  _getDeviceInfo() {
    try {
      const ua = navigator.userAgent || '';
      let browser = 'Unknown';
      let os = 'Unknown';
      let deviceType = 'Desktop';

      // Browser detection
      if (ua.includes('Firefox/')) browser = 'Firefox';
      else if (ua.includes('Edg/')) browser = 'Edge';
      else if (ua.includes('Chrome/')) browser = 'Chrome';
      else if (ua.includes('Safari/') && !ua.includes('Chrome')) browser = 'Safari';
      else if (ua.includes('Opera') || ua.includes('OPR/')) browser = 'Opera';

      // OS detection
      if (ua.includes('Windows')) os = 'Windows';
      else if (ua.includes('Mac OS')) os = 'macOS';
      else if (ua.includes('Android')) os = 'Android';
      else if (ua.includes('iPhone') || ua.includes('iPad')) os = 'iOS';
      else if (ua.includes('Linux')) os = 'Linux';

      // Device type detection
      if (/Mobi|Android|iPhone|iPad|iPod/i.test(ua)) {
        deviceType = /iPad|Tablet/i.test(ua) ? 'Tablet' : 'Mobile';
      }

      return { browser, os, deviceType, userAgent: ua.substring(0, 200) };
    } catch {
      return { browser: 'Unknown', os: 'Unknown', deviceType: 'Unknown', userAgent: '' };
    }
  }

  /**
   * Log an audit event
   * @param {Object} params
   * @param {string} params.action - AUDIT_ACTIONS enum value
   * @param {string} params.collection - Target collection name
   * @param {string} params.documentId - Target document ID
   * @param {string} params.performedBy - User ID
   * @param {string} params.performedByName - User display name
   * @param {string} params.performedByRole - User role
   * @param {string} params.description - Human-readable description
   * @param {Object} [params.oldValue] - Previous state
   * @param {Object} [params.newValue] - New state
   * @param {Object} [params.metadata] - Additional context
   */
  async log({
    action,
    collection: targetCollection,
    documentId,
    performedBy,
    performedByName,
    performedByRole,
    description,
    oldValue = null,
    newValue = null,
    metadata = null,
  }) {
    try {
      const deviceInfo = this._getDeviceInfo();

      const logEntry = {
        action,
        collection: targetCollection,
        documentId,
        performedBy,
        performedByName,
        performedByRole,
        performedAt: Timestamp.now(),
        description,
        oldValue,
        newValue,
        metadata,
        device: deviceInfo,
        createdAt: Timestamp.now(),
      };

      // Remove null/undefined values (but keep device)
      Object.keys(logEntry).forEach((key) => {
        if (logEntry[key] === null || logEntry[key] === undefined) {
          delete logEntry[key];
        }
      });

      await addDoc(
        collection(examDb, COLLECTIONS.AUDIT_LOGS),
        logEntry
      );
    } catch (error) {
      // Audit logging should never break the main flow
      console.error('Audit log failed:', error);
    }
  }

  /**
   * Create a pre-configured logger for a specific user session
   * @param {Object} user - Current user profile
   * @returns {Function} Logger function
   */
  createLogger(user) {
    return (params) =>
      this.log({
        ...params,
        performedBy: user.uid,
        performedByName: user.displayName,
        performedByRole: user.role,
      });
  }
}

export const auditService = new AuditService();
export default auditService;

